import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import ActivateUserModal from '@/components/ActivateUserModal';
import ActiveSessionsModal, { type ActiveSession } from '@/components/ActiveSessionsModal';
import { ErrorBlock, LoadingBlock } from '@/components/activities/ActivityUi';
import AssignBadgeModal from '@/components/AssignBadgeModal';
import { ParticipantsCard, PendingCard, TopEquipmentCard } from '@/components/dashboard/DashboardSide';
import DashboardTiles from '@/components/dashboard/DashboardTiles';
import LiveEquipment from '@/components/dashboard/LiveEquipment';
import QuickActions, { type QuickAction } from '@/components/dashboard/QuickActions';
import TodaySessions from '@/components/dashboard/TodaySessions';
import SuspendUserModal from '@/components/SuspendUserModal';
import UnlockUserModal from '@/components/UnlockUserModal';
import { useDigitalCenters } from '@/hooks/useDigitalCenters';
import { useDashboard, useEndEquipmentSession, useParticipantStats } from '@/hooks/useReports';
import { isSuperAdmin } from '@/lib/staffPermissions';
import { useAuthStore } from '@/stores/auth';

const CENTRE_KEY = 'dashboard.centro';

/** The centre last chosen on this browser. Zero is every centre. */
function rememberedCentre(): number {
  try {
    const id = Number(window.localStorage.getItem(CENTRE_KEY));
    return Number.isInteger(id) && id > 0 ? id : 0;
  } catch {
    return 0;
  }
}

function rememberCentre(id: number) {
  try {
    window.localStorage.setItem(CENTRE_KEY, String(id));
  } catch {
    // Private browsing: the choice lasts for this visit only.
  }
}

type Modal = QuickAction | 'sessions' | null;

/** What is happening today: who is in, what is on, what is in use. */
export default function DashboardPage() {
  const { user } = useAuthStore();
  const [centerId, setCenterId] = useState(rememberedCentre);
  const [modal, setModal] = useState<Modal>(null);

  const centersQuery = useDigitalCenters({ limit: 100 });
  const centers = centersQuery.data?.digitalCenters ?? [];
  const { data: dashboard, isLoading, isError } = useDashboard(centerId);
  const { data: participants } = useParticipantStats();
  const endSession = useEndEquipmentSession();

  const chooseCentre = (id: number) => {
    setCenterId(id);
    rememberCentre(id);
  };

  const sessions: ActiveSession[] = (dashboard?.equipmentSessions ?? []).map((s) => ({
    id: String(s.id),
    userName: s.participantName,
    userEmail: '',
    equipment: s.equipmentName,
    startTime: new Date(s.startedAt),
    expiresAt: s.expiresAt ? new Date(s.expiresAt) : null,
  }));

  const close = () => setModal(null);

  return (
    <>
      <Helmet>
        <title>Dashboard | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Bem-vindo, <span className="font-medium text-foreground">{user?.name}</span>. Isto é o que se passa hoje.
            </p>
          </div>
          <div className="flex flex-col gap-1 min-w-[220px]">
            <label htmlFor="dashboard-centre" className="text-xs font-medium text-muted-foreground">
              Centro
            </label>
            <select
              id="dashboard-centre"
              className="input"
              value={centerId}
              onChange={(e) => chooseCentre(Number(e.target.value))}
            >
              <option value={0}>Todos os centros</option>
              {centers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.centerName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading && <LoadingBlock />}
        {isError && !dashboard && <ErrorBlock message="Não foi possível carregar o dashboard. Tente novamente." />}

        {dashboard && (
          <>
            <DashboardTiles dashboard={dashboard} />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
              <div className="xl:col-span-2 space-y-6">
                <TodaySessions sessions={dashboard.sessionsToday} />
                <LiveEquipment sessions={dashboard.equipmentSessions} onManage={() => setModal('sessions')} />
              </div>
              <div className="space-y-6">
                <PendingCard pending={dashboard.pending} canManageStaff={isSuperAdmin(user)} />
                <ParticipantsCard stats={participants} />
                <TopEquipmentCard equipment={dashboard.topEquipment} />
              </div>
            </div>
          </>
        )}

        <QuickActions onAction={setModal} />
      </div>

      <UnlockUserModal isOpen={modal === 'unlock'} onClose={close} />
      <ActiveSessionsModal
        isOpen={modal === 'sessions'}
        onClose={close}
        sessions={sessions}
        onEndSession={async (id) => {
          await endSession.mutateAsync(Number(id));
        }}
      />
      <AssignBadgeModal isOpen={modal === 'badge'} onClose={close} />
      <SuspendUserModal isOpen={modal === 'suspend'} onClose={close} />
      <ActivateUserModal isOpen={modal === 'activate'} onClose={close} />
    </>
  );
}
